import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import ModalInfoCertification from 'front-office/components/shared/info-certificate';
import ModalCertificate from 'front-office/components/lever/request-certificate-modal';
import ModalPrintSuccess from 'front-office/components/shared/request-print';
import { Fragment, useCallback, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import * as yup from 'yup';
import { LeverFormVM } from 'front-office/view-models/lever/lever-form';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
// import { openPdf } from 'shell/utils/open-pdf';
import LeverForm from 'front-office/components/lever';
import LeverPageButtons from 'front-office/components/shared/buttons-certificate';
import { validationCertificationSchema } from 'front-office/constants/validation-shema/certification-schema';
import { LeverCrumbs } from 'front-office/constants/lever-crumbs';
import { transformRequestLeverData } from 'front-office/utils/transform-request-lever';
import { ResponseCertificateHistorical } from 'shell/models/response-certificate-historical';
import { transformResponseRequestCertificateHistorical } from 'front-office/utils/transform-response-certificate-historical';
import { CommonRequestCertificateInterface } from 'front-office/view-models/common-certificate-form';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';
import PmiTitle from 'shell/components/shared/title';

const validationSchema = yup
  .object()
  .shape({
    ...validationCertificationSchema,
  })
  .test('', '', (values) => {
    const valuesCasted = values as unknown as LeverFormVM;

    const accountHolder = values?.accountholder as AccordionAccountholderFormVM | undefined;
    const applicant = values?.applicant as AccordionApplicantFormVM;

    // same as applicant
    if (valuesCasted?.accountholder?.sameApplicant) {
      if (!valuesCasted?.certifiableApplicant) {
        return new yup.ValidationError(
          'Soggetto non certificabile',
          undefined,
          'certifiableApplicant'
        );
      }
    } else {
      if (accountHolder?.account?.applicant?.fiscalCode === applicant?.fiscalCode) {
        return new yup.ValidationError(
          `Richiedente è uguale all'intestatario`,
          undefined,
          'applicant.fiscalCode'
        );
      }
      if (!valuesCasted?.accountholder?.certifiableAccountholder) {
        return new yup.ValidationError(
          'Soggetto non certificabile',
          undefined,
          'certifiableAccountholder'
        );
      }
    }

    return true;
  });

const LeverPage = (): React.ReactElement => {
  const [certificatePdf, getPdfCertificate] = useHttpFetch<
    HttpResponse<ResponseCertificateHistorical>,
    HttpResponse<ResponseCertificateHistorical>
  >(`${__CERTIFICATION_API_URL__}/richiesta-certificato/storico/leva`, {
    onBootstrap: false,
    options: {
      method: 'POST',
    },
    transformer: transformResponseRequestCertificateHistorical,
  });

  const castedCertificatePdf = certificatePdf.data as HttpResponse<ResponseCertificateHistorical>;

  // ModalCertificate
  const [open, setOpen] = useState<boolean>(false);

  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{ open: boolean; total?: number }>({
    open: false,
  });

  // InfoCertification
  const [infoCertification, setInfoCertification] = useState<{ open: boolean; total?: number }>({
    open: false,
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  // Handle the submit
  const handleSubmit = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const onRequestCertificate = useEventCallback((values: LeverFormVM) => {
    const data = transformRequestLeverData(values);

    (
      getPdfCertificate({ data }) as ResponsePromise<
        HttpResponse<ResponseCertificateHistorical>,
        HttpResponse<ResponseCertificateHistorical>
      >
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCertificateHistorical>;
      const pdfCertificate = responseCasted?.payload?.contenutoPdf;

      const { modality } = values;

      const {
        amountRightsSecretariat = 0.0,
        amountRightsCivilState = 0.0,
        amountFixedRights = 0.0,
        amountExpenses = 0.0,
        amountTaxStamp = 0.0,
      } = modality || {};

      const rights =
        amountExpenses +
        amountFixedRights +
        amountRightsCivilState +
        amountRightsSecretariat +
        amountTaxStamp;

      // close ModalCertificate
      setOpen(false);

      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({ data: pdfCertificate, name: 'Leva', open: true });
      }

      const parsedTotal = values?.certificateInternOnly
        ? Number.parseFloat('0,00')
        : Number.parseFloat(rights?.toString() || '0,00');

      // const total = (values?.payment || 0) + parsedTotal;

      // context.setFieldValue('payment', total);

      //open ModalPrintSuccess
      setModalPrintSuccess({ open: false, total: parsedTotal });
    });
  });

  const onYes = useEventCallback(
    (
      values: CommonRequestCertificateInterface,
      setFieldValue: (
        key: keyof CommonRequestCertificateInterface,
        value: CommonRequestCertificateInterface[keyof CommonRequestCertificateInterface]
      ) => void
    ) => {
      const total = (values?.payment || 0) + (modalPrintSuccess?.total || 0);

      setFieldValue('payment', total);

      // close ModalPrintSuccess
      setModalPrintSuccess({ open: false, total: undefined });

      //open InfoCertification
      setInfoCertification({ open: true, total: modalPrintSuccess.total });
    }
  );

  const { userProfile } = useUserProfile();
  const cashManagement = userProfile?.datiLogin?.profile?.servizio?.gestioneCassa;

  return (
    <Fragment>
      <Breadcrumbs items={LeverCrumbs} base="sportello" />
      <div>
        <PmiTitle title="Certificazione Leva" />
      </div>
      <div className="pmi-mt-4">
        <div className="pmi-mt-2">
          <Formik
            validationSchema={validationSchema}
            initialValues={
              {
                applicant: { fiscalCode: undefined },
                accountholder: { sameApplicant: true },
                payment: 0,
                certificateInternOnly: cashManagement !== 'S' ? true : false,
                intendedUse: '0',
                generality: {
                  isForeignDocument: DocumentRadioCertificate.IdentityCard,
                },
              } as LeverFormVM
            }
            onSubmit={(values) => onRequestCertificate(values)}
            validateOnChange
            validateOnBlur
            // validateOnMount
          >
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={12} md={3}>
                  <LeverPageButtons handleRequestCertificate={handleSubmit} />
                </Grid>
                <Grid item xs={12} md={9}>
                  <div className="pmi-px-md-4">
                    <LeverForm />
                  </div>
                </Grid>
              </Grid>
              <ModalCertificate open={open} onClose={setOpen} />
              <ModalPrintSuccess
                open={modalPrintSuccess.open}
                onClose={() => setModalPrintSuccess({ open: false })}
                onYes={onYes}
              />
              <ModalInfoCertification
                open={infoCertification.open}
                onClose={() => setInfoCertification({ open: false })}
                title={castedCertificatePdf?.payload?.numeroIdentificativoDocumento}
                amountCertifate={infoCertification.total?.toFixed(2)}
              />
              <ModalViewerPDF
                open={pdfModal.open}
                data={pdfModal.data}
                name={pdfModal.name}
                title="Certificazione"
                onClose={() => {
                  setPdfModal({ data: '', open: false, name: '' });
                  setModalPrintSuccess((oldState) => ({ ...oldState, open: true }));
                }}
              />
            </Form>
          </Formik>
        </div>
      </div>
    </Fragment>
  );
};

LeverPage.displayName = 'Lever page';

export default LeverPage;
